import { useState, useMemo, useDeferredValue, ChangeEvent, useCallback } from "react";
import { Table, TableBody, TableHead, TableHeader, TableRow, Button, Checkbox } from "@libs/zapi-ui/shadcn";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { SearchInput, LoadingSpinner } from "@libs/zapi-ui/custom";
import { InviteUserModal } from "./InviteUserModal";
import { ManageAccessDrawer } from "./ManageAccessDrawer";
import { toast } from "sonner";
import { getOrganizationData } from "../services/getOrganizationData";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TGetUsersResponse, TUser } from "../services/schemas";
import { TeamTableRow } from "./TeamTableRow";

export function TeamTable() {
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAccessDrawerOpen, setIsAccessDrawerOpen] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState<TUser | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organizationId: defaultOrganizationId, id: currentUserId, role } = useMeStore();
  const { currentOrganizationId } = useOrganizationStore();

  const organizationId =
    isSystemAdmin(role) && currentOrganizationId > 0 ? currentOrganizationId : defaultOrganizationId;

  const { data: organizationData, isLoading: isLoadingOrganizationData } = useQuery<TGetUsersResponse, Error>({
    queryKey: ["organizationData", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      try {
        const response = await getOrganizationData(organizationId);
        return response.map((user) => ({ ...user, createdAt: new Date(user.createdAt) }));
      } catch (error) {
        console.error(error);
        toast.error("Error fetching organization data");
        navigate(getRouteProjects());
        throw error;
      }
    },
    enabled: !!organizationId
  });

  const filteredTeamMembers = useMemo(() => {
    if (!organizationData) return [];
    if (!deferredSearchQuery) return organizationData;
    return organizationData.filter(
      (member) =>
        member.firstName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.lastName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.email.toLowerCase().startsWith(deferredSearchQuery.toLowerCase())
    );
  }, [deferredSearchQuery, organizationData]);

  const displayedMemberIds = useMemo(() => filteredTeamMembers.map((member) => member.id), [filteredTeamMembers]);

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) setSelectedRows(displayedMemberIds);
    else setSelectedRows([]);
  };

  const handleSelectRow = useCallback((id: number) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  }, []);

  const handleRemoveSelectedUsers = () => {
    toast.success(`${selectedRows.length} user(s) submitted for removal.`);
    setSelectedRows([]);
  };

  const handleOpenManageAccess = useCallback((user: TUser) => {
    setSelectedUserForAccess(user);
    setIsAccessDrawerOpen(true);
  }, []);

  const handleDrawerClose = (openState: boolean) => {
    setIsAccessDrawerOpen(openState);
    if (!openState) {
      setSelectedUserForAccess(null);
    }
  };

  const isAllSelected = displayedMemberIds.length > 0 && selectedRows.length === displayedMemberIds.length;

  const currentPage = 1;
  const totalPages = 1;

  if (isLoadingOrganizationData || !organizationId) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4 w-full">
      <div>
        <h1 className="text-xl font-medium">Team</h1>
      </div>
      <div className="flex items-center justify-between">
        <SearchInput
          placeholder="Search Members"
          className="w-[320px]"
          value={searchQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        />
        <div className="flex items-center space-x-3">
          {selectedRows.length > 0 ? (
            <>
              <span className="text-sm text-zinc-900 font-medium">
                {selectedRows.length} User{selectedRows.length > 1 ? "s" : ""} Selected
              </span>
              <Button variant="secondary" onClick={handleRemoveSelectedUsers} className="bg-zinc-200">
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsInviteModalOpen(true)}>
              <Plus className="h-4 w-4" />
              User
            </Button>
          )}
        </div>
      </div>
      <div className="rounded-md border w-full h-[calc(100vh-300px)] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all rows"
                  disabled
                />
              </TableHead>
              <TableHead className="w-[250px] normal-case">User</TableHead>
              <TableHead className="normal-case">Role</TableHead>
              <TableHead className="normal-case">Projects</TableHead>
              <TableHead className="normal-case">Account Status</TableHead>
              {/* <TableHead className="normal-case">Last Login</TableHead> */}
              <TableHead className="normal-case">Date Added</TableHead>
              <TableHead className="w-[150px] normal-case">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTeamMembers.map((member) => {
              return (
                <TeamTableRow
                  key={member.id}
                  member={member}
                  organizationId={organizationId}
                  currentUserId={currentUserId}
                  selectedRows={selectedRows}
                  handleSelectRow={handleSelectRow}
                  handleOpenManageAccess={handleOpenManageAccess}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" disabled={currentPage === 1}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          {[1].map((page) => (
            <Button
              key={page}
              variant={currentPage === page ? "default" : "ghost"}
              size="sm"
              className="w-6 h-6 p-0 rounded-full"
            >
              {page}
            </Button>
          ))}
          {totalPages > 1 && <span className="text-sm">...</span>}
          {totalPages > 1 && (
            <Button
              variant={currentPage === totalPages ? "default" : "ghost"}
              size="sm"
              className="w-6 h-6 p-0 rounded-full"
            >
              {totalPages}
            </Button>
          )}
          <Button variant="ghost" size="sm" disabled={currentPage === totalPages}>
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <span className="text-sm text-muted-foreground">
          Showing {filteredTeamMembers.length} of {organizationData?.length || 0} results
        </span>
      </div>

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onOpenChange={setIsInviteModalOpen}
        organizationId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
        }}
      />
      <ManageAccessDrawer
        open={isAccessDrawerOpen}
        onOpenChange={handleDrawerClose}
        user={selectedUserForAccess as TUser}
        availableProjects={[]}
        orgId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
        }}
      />
    </div>
  );
}
